import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Card, CardContent, PageHeader, Tab, TabList, TabPanel, Tabs } from '../../components/ui';
import { Button } from '../../components/ui/Button';
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  Play,
  RefreshCw,
  Square,
  Trash2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm } from '../../lib/themed-dialogs';

const ENGINE_URL = 'https://10.119.251.116:3010';
const DOC_TYPES = ['invoice', 'confirmation', 'offer', 'status_update'] as const;

interface Status {
  ok: boolean;
  state?: string;
  running?: boolean;
  container?: string | null;
  started_at?: string;
  url?: string;
}

// ── Engine tab ────────────────────────────────────────────────────────────────

function EngineTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, refetch } = useQuery<Status>({
    queryKey: ['polydoc-status'],
    queryFn: () => api.mcp<Status>('polydoc.deploy', { action: 'status' }),
    refetchInterval: 15_000,
  });

  const actionMut = useMutation({
    mutationFn: (action: string) => api.mcp('polydoc.deploy', { action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['polydoc-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const running = data?.running ?? false;
  const state = data?.state ?? '—';
  const pending = actionMut.isPending;

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='p-5'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div
                className={`w-3 h-3 rounded-full ${
                  running
                    ? 'bg-emerald-400'
                    : state === 'not_deployed'
                    ? 'bg-gray-600'
                    : 'bg-amber-400'
                }`}
              />
              <div>
                <div className='text-sm font-semibold text-white'>
                  {data?.container ?? 'polydoc-engine'}
                </div>
                <div className='text-xs text-gray-500 capitalize'>{state}</div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              {running && (
                <a
                  href={ENGINE_URL}
                  target='_blank'
                  rel='noopener'
                  className='flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300'
                >
                  <ExternalLink size={11} /> {t('polydoc.engine')}
                </a>
              )}
              <Button
                variant='ghost'
                size='icon'
                icon={<RefreshCw size={13} />}
                onClick={() => refetch()}
              />
            </div>
          </div>

          {data?.started_at && (
            <div className='mt-3 text-xs text-gray-600'>
              {t('polydoc.startedAt', { time: new Date(data.started_at).toLocaleString('cs') })}
            </div>
          )}

          <div className='flex gap-2 mt-4 flex-wrap'>
            {state === 'not_deployed'
              ? (
                <Button
                  variant='primary'
                  size='sm'
                  loading={pending}
                  icon={<Play size={12} />}
                  onClick={() => actionMut.mutate('deploy')}
                >
                  {t('polydoc.deployEngine')}
                </Button>
              )
              : (
                <>
                  {!running && (
                    <Button
                      variant='success'
                      size='sm'
                      loading={pending}
                      icon={<Play size={12} />}
                      onClick={() => actionMut.mutate('start')}
                    >
                      {t('polydoc.start')}
                    </Button>
                  )}
                  {running && (
                    <>
                      <Button
                        variant='warning'
                        size='sm'
                        loading={pending}
                        icon={<RefreshCw size={12} />}
                        onClick={() => actionMut.mutate('update')}
                      >
                        {t('polydoc.updateGitPull')}
                      </Button>
                      <Button
                        variant='secondary'
                        size='sm'
                        loading={pending}
                        icon={<Square size={12} />}
                        onClick={() => actionMut.mutate('stop')}
                      >
                        {t('polydoc.stop')}
                      </Button>
                    </>
                  )}
                  <Button
                    variant='danger'
                    size='sm'
                    loading={pending}
                    icon={<Trash2 size={12} />}
                    onClick={async () => {
                      if (await themedConfirm(t('polydoc.confirmRemove'))) actionMut.mutate('remove');
                    }}
                  >
                    {t('polydoc.remove')}
                  </Button>
                </>
              )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Validate tab ──────────────────────────────────────────────────────────────

function ValidateTab() {
  const { t } = useTranslation();
  const [json, setJson] = useState('');
  const [result, setResult] = useState<
    { valid?: boolean; errors?: string[]; error?: string } | null
  >(null);

  const loadMut = useMutation({
    mutationFn: (type: string) =>
      api.mcp<{ schema: unknown }>('polydoc.render', { get_schema: type }),
    onSuccess: (r) => setJson(JSON.stringify(r.schema, null, 2)),
    onError: (e) => toast.error((e as Error).message),
  });

  const validateMut = useMutation({
    mutationFn: () => {
      let doc: unknown;
      try {
        doc = JSON.parse(json);
      } catch {
        throw new Error(t('polydoc.invalidJson'));
      }
      return api.mcp<typeof result>('polydoc.validate', { document: doc as Record<string, unknown> });
    },
    onSuccess: setResult,
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <span className='text-xs text-gray-500'>{t('polydoc.loadSample')}</span>
        {DOC_TYPES.map((dt) => (
          <button
            key={dt}
            onClick={() => loadMut.mutate(dt)}
            className='px-2 py-1 rounded-lg text-xs bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 transition-colors'
          >
            {dt}
          </button>
        ))}
      </div>

      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        rows={18}
        placeholder='{"header": {"format": "poly/1.0", "doc_id": "...", "doc_type": "invoice"}, ...}'
        className='w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-blue-500 resize-y'
      />

      <Button
        variant='primary'
        size='sm'
        loading={validateMut.isPending}
        icon={<CheckCircle2 size={13} />}
        onClick={() => validateMut.mutate()}
      >
        {t('polydoc.validate')}
      </Button>

      {result && (
        <div
          className={`rounded-xl border p-4 ${
            result.valid
              ? 'border-emerald-500/20 bg-emerald-500/5'
              : 'border-red-500/20 bg-red-500/5'
          }`}
        >
          <div
            className={`flex items-center gap-2 font-semibold text-sm mb-2 ${
              result.valid ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {result.valid ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {result.valid
              ? t('polydoc.documentValid')
              : t('polydoc.errorCount', { count: result.errors?.length ?? 1 })}
          </div>
          {result.errors?.map((e, i) => (
            <div key={i} className='text-xs text-red-300 font-mono mt-1'>• {e}</div>
          ))}
          {result.error && <div className='text-xs text-red-300 font-mono'>{result.error}</div>}
        </div>
      )}
    </div>
  );
}

// ── Render tab ────────────────────────────────────────────────────────────────

function RenderTab() {
  const { t } = useTranslation();
  const [json, setJson] = useState('');
  const [result, setResult] = useState<
    { ok?: boolean; html_url?: string; errors?: string[]; error?: string } | null
  >(null);

  const loadMut = useMutation({
    mutationFn: (type: string) =>
      api.mcp<{ schema: unknown }>('polydoc.render', { get_schema: type }),
    onSuccess: (r) => setJson(JSON.stringify(r.schema, null, 2)),
    onError: (e) => toast.error((e as Error).message),
  });

  const renderMut = useMutation({
    mutationFn: () => {
      let doc: unknown;
      try {
        doc = JSON.parse(json);
      } catch {
        throw new Error(t('polydoc.invalidJson'));
      }
      return api.mcp<typeof result>('polydoc.render', {
        document: doc as Record<string, unknown>,
      });
    },
    onSuccess: setResult,
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <span className='text-xs text-gray-500'>{t('polydoc.loadSample')}</span>
        {DOC_TYPES.map((dt) => (
          <button
            key={dt}
            onClick={() => loadMut.mutate(dt)}
            className='px-2 py-1 rounded-lg text-xs bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 transition-colors'
          >
            {dt}
          </button>
        ))}
      </div>

      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        rows={18}
        placeholder='{"header": {"format": "poly/1.0", "doc_id": "...", "doc_type": "invoice"}, ...}'
        className='w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-blue-500 resize-y'
      />

      <Button
        variant='primary'
        size='sm'
        loading={renderMut.isPending}
        icon={<Play size={13} />}
        onClick={() => renderMut.mutate()}
      >
        {t('polydoc.render')}
      </Button>

      {result && (
        <div
          className={`rounded-xl border p-4 ${
            result.ok ? 'border-blue-500/20 bg-blue-500/5' : 'border-red-500/20 bg-red-500/5'
          }`}
        >
          {result.ok
            ? (
              <div className='space-y-2'>
                <div className='flex items-center gap-2 text-emerald-400 font-semibold text-sm'>
                  <CheckCircle2 size={14} /> {t('polydoc.documentRendered')}
                </div>
                {result.html_url && (
                  <a
                    href={result.html_url}
                    target='_blank'
                    rel='noopener'
                    className='flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300'
                  >
                    <ExternalLink size={13} /> {result.html_url}
                  </a>
                )}
              </div>
            )
            : (
              <>
                {result.errors?.map((e, i) => (
                  <div key={i} className='text-xs text-red-300 font-mono'>• {e}</div>
                ))}
                {result.error && (
                  <div className='text-xs text-red-300 font-mono'>{result.error}</div>
                )}
              </>
            )}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function PolyDocModule() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='PolyDoc'
        description={t('polydoc.subtitle')}
        icon={FileText}
      />

      <Tabs defaultTab='engine'>
        <TabList>
          <Tab id='engine'>{t('polydoc.tabEngine')}</Tab>
          <Tab id='validate'>{t('polydoc.tabValidate')}</Tab>
          <Tab id='render'>{t('polydoc.tabRender')}</Tab>
        </TabList>
        <TabPanel id='engine'>
          <EngineTab />
        </TabPanel>
        <TabPanel id='validate'>
          <ValidateTab />
        </TabPanel>
        <TabPanel id='render'>
          <RenderTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}
